import {
  Alert,
  Box,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  SegmentedControl,
  Stack,
  Text,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { modals } from "@mantine/modals"
import { nextStepToast } from "@dehub/theme"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useInboundMutations } from "./hooks"
import { IAuth } from "../../store/Auth"
import type { ICreateInboundInput } from "../../apis/inbound-order"
import {
  InboundSourcePanel,
  InboundItemsTable,
  emptyCatalogRow,
  emptyUncataloguedRow,
  type CatalogVariantPick,
  type InboundRow,
  type CatalogRow,
  type UncataloguedRow,
  type SourceType,
} from "./atoms"

interface Props {
  opened: boolean
  onClose: () => void
  onCreated?: (id: string) => void
}

interface FormShape {
  sourceType: SourceType
  fromWarehouseId: string
  toWarehouseId: string
  externalPartyId: string
  externalPartyContactName: string
  externalPartyContactPhone: string
  externalPartyAddressOverride: string
  items: InboundRow[]
}

const initial = (defaultWarehouseId: string): FormShape => ({
  sourceType: "INTERNAL_WAREHOUSE",
  fromWarehouseId: "",
  toWarehouseId: defaultWarehouseId,
  externalPartyId: "",
  externalPartyContactName: "",
  externalPartyContactPhone: "",
  externalPartyAddressOverride: "",
  items: [emptyCatalogRow()],
})

export function CreateInboundOrderDialog({ opened, onClose, onCreated }: Props) {
  const { t } = useTranslation("inbound")
  const mutations = useInboundMutations()
  const user = useSelector((s: { auth: IAuth }) => s.auth.user)
  const preferredWarehouseId =
    (user as { preferredWarehouseId?: string | null } | null)?.preferredWarehouseId ?? ""

  const form = useForm<FormShape>({
    initialValues: initial(preferredWarehouseId),
    validate: (values) => {
      const errs: Record<string, string> = {}
      if (!values.toWarehouseId.trim()) {
        errs.toWarehouseId = t("create.errNoDestination")
      }
      if (values.sourceType === "INTERNAL_WAREHOUSE") {
        if (!values.fromWarehouseId.trim()) {
          errs.fromWarehouseId = t("create.errNoSource")
        } else if (values.fromWarehouseId === values.toWarehouseId) {
          errs.fromWarehouseId = t("create.errSameWarehouse")
        }
      } else if (!values.externalPartyId.trim()) {
        errs.externalPartyId = t("create.errNoExternalParty")
      }
      values.items.forEach((row, idx) => {
        if (row.kind === "catalog" && !row.productId) {
          errs[`items.${idx}.productLabel`] = t("create.errNoProduct")
        }
        if (row.kind === "uncatalogued" && !row.uncataloguedItemName.trim()) {
          errs[`items.${idx}.uncataloguedItemName`] = t("create.errNoItemName")
        }
        if (!row.unit.trim()) {
          errs[`items.${idx}.unit`] = t("create.errNoUnit")
        }
        if (!row.requestedQuantity || row.requestedQuantity <= 0) {
          errs[`items.${idx}.requestedQuantity`] = t("create.errQuantity")
        }
      })
      return errs
    },
  })

  const sourceType = form.values.sourceType
  const allowUncatalogued = sourceType === "EXTERNAL_PARTY"

  const updateRow = (idx: number, patch: Partial<CatalogRow> | Partial<UncataloguedRow>) => {
    form.setFieldValue(
      `items.${idx}`,
      { ...form.values.items[idx], ...patch } as InboundRow,
    )
  }

  const pickCatalog = (idx: number, pick: CatalogVariantPick) => {
    const current = form.values.items[idx] as CatalogRow
    form.setFieldValue(`items.${idx}`, {
      ...current,
      productId: pick.productId,
      productLabel: pick.label,
      unit: current.unit && current.unit.trim() ? current.unit : pick.unit ?? "ш",
    } as CatalogRow)
  }

  const addCatalog = () => form.insertListItem("items", emptyCatalogRow())
  const addUncatalogued = () => form.insertListItem("items", emptyUncataloguedRow())

  const removeRow = (idx: number) => {
    if (form.values.items.length === 1) {
      const current = form.values.items[0]
      const replacement =
        current.kind === "uncatalogued" ? emptyUncataloguedRow() : emptyCatalogRow()
      form.setFieldValue("items.0", replacement as InboundRow)
      return
    }
    form.removeListItem("items", idx)
  }

  const handleSubmit = async (values: FormShape) => {
    const payload: ICreateInboundInput = {
      sourceType: values.sourceType,
      toWarehouseId: values.toWarehouseId,
      items: values.items.map((row) => {
        if (row.kind === "catalog") {
          return {
            productId: row.productId,
            unit: row.unit,
            requestedQuantity: Number(row.requestedQuantity ?? 0),
            unitCost: row.unitCost != null ? Number(row.unitCost) : null,
          }
        }
        return {
          uncataloguedItemName: row.uncataloguedItemName.trim(),
          uncataloguedSku: row.uncataloguedSku.trim() || null,
          unit: row.unit,
          requestedQuantity: Number(row.requestedQuantity ?? 0),
          unitCost: row.unitCost != null ? Number(row.unitCost) : null,
        }
      }),
    }

    if (values.sourceType === "INTERNAL_WAREHOUSE") {
      payload.fromWarehouseId = values.fromWarehouseId
    } else {
      payload.externalPartyId = values.externalPartyId
      if (values.externalPartyContactName.trim()) {
        payload.externalPartyContactName = values.externalPartyContactName.trim()
      }
      if (values.externalPartyContactPhone.trim()) {
        payload.externalPartyContactPhone = values.externalPartyContactPhone.trim()
      }
      if (values.externalPartyAddressOverride.trim()) {
        payload.externalPartyAddressOverride = values.externalPartyAddressOverride.trim()
      }
    }

    try {
      const created = await mutations.create(payload)
      nextStepToast("order.inbound.toast.create", { fallback: { title: t("create.success") } })
      form.setValues(initial(preferredWarehouseId))
      onClose()
      onCreated?.(created.id)
    } catch {
      // surfaced via mutations.error
    }
  }

  const attemptClose = () => {
    if (mutations.submitting) return
    if (form.isDirty()) {
      modals.openConfirmModal({
        title: t("create.confirmDiscardTitle"),
        children: <Text size="sm">{t("create.confirmDiscardBody")}</Text>,
        labels: { confirm: t("create.confirmExit"), cancel: t("create.confirmStay") },
        confirmProps: { color: "red" },
        onConfirm: () => {
          form.setValues(initial(preferredWarehouseId))
          onClose()
        },
      })
      return
    }
    onClose()
  }

  const rowErrors = form.values.items.map((_, idx) => ({
    productLabel: (form.errors[`items.${idx}.productLabel`] as string) ?? null,
    uncataloguedItemName:
      (form.errors[`items.${idx}.uncataloguedItemName`] as string) ?? null,
    unit: (form.errors[`items.${idx}.unit`] as string) ?? null,
    requestedQuantity:
      (form.errors[`items.${idx}.requestedQuantity`] as string) ?? null,
  }))

  return (
    <Modal
      opened={opened}
      onClose={attemptClose}
      title={t("create.title")}
      size="xl"
      padding="lg"
    >
      <Box pos="relative" component="form" onSubmit={form.onSubmit(handleSubmit)}>
        <LoadingOverlay visible={mutations.submitting} />
        <Stack gap="md">
          <SegmentedControl
            value={sourceType}
            onChange={(v) => form.setFieldValue("sourceType", v as SourceType)}
            data={[
              { value: "INTERNAL_WAREHOUSE", label: t("sourceType.INTERNAL_WAREHOUSE") },
              { value: "EXTERNAL_PARTY", label: t("sourceType.EXTERNAL_PARTY") },
            ]}
            fullWidth
          />

          <InboundSourcePanel
            sourceType={sourceType}
            fromWarehouseId={form.values.fromWarehouseId}
            toWarehouseId={form.values.toWarehouseId}
            externalPartyId={form.values.externalPartyId}
            externalPartyContactName={form.values.externalPartyContactName}
            externalPartyContactPhone={form.values.externalPartyContactPhone}
            externalPartyAddressOverride={form.values.externalPartyAddressOverride}
            errors={{
              fromWarehouseId: form.errors.fromWarehouseId as string | undefined,
              toWarehouseId: form.errors.toWarehouseId as string | undefined,
              externalPartyId: form.errors.externalPartyId as string | undefined,
            }}
            onFromWarehouseChange={(v) => form.setFieldValue("fromWarehouseId", v)}
            onToWarehouseChange={(v) => form.setFieldValue("toWarehouseId", v)}
            onExternalPartyChange={(v) => form.setFieldValue("externalPartyId", v)}
            onContactNameChange={(v) =>
              form.setFieldValue("externalPartyContactName", v)
            }
            onContactPhoneChange={(v) =>
              form.setFieldValue("externalPartyContactPhone", v)
            }
            onAddressOverrideChange={(v) =>
              form.setFieldValue("externalPartyAddressOverride", v)
            }
          />

          <Stack gap="xs">
            <Text size="sm" c="dimmed" tt="uppercase" fw={600}>
              {t("create.itemsList")}
            </Text>
            <InboundItemsTable
              rows={form.values.items}
              errors={rowErrors}
              allowUncatalogued={allowUncatalogued}
              onChange={updateRow}
              onPickCatalog={pickCatalog}
              onAddCatalog={addCatalog}
              onAddUncatalogued={addUncatalogued}
              onRemove={removeRow}
            />
          </Stack>

          {mutations.error && (
            <Alert color="red" variant="light" title={t("create.createError")}>
              {mutations.error.message}
            </Alert>
          )}

          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" onClick={attemptClose}>
              {t("create.cancel")}
            </Button>
            <Button type="submit" loading={mutations.submitting}>
              {t("create.submit")}
            </Button>
          </Group>
        </Stack>
      </Box>
    </Modal>
  )
}
